import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Sparkles, ShoppingBag, Store, Wallet, Gift } from "lucide-react";
import { T, tr } from "../lib/i18n";
import { loyaltyToken, loyaltyMe, loyaltyLogout } from "../lib/api";

/**
 * Bottom action bar shared by ALL public guest pages. Valuta first, Fidelity
 * last; "Menu" is not here because every page already has it in the top bar.
 * The current page is filtered out. `current` is one of:
 *   "menu" | "valuta" | "asporto" | "shop" | "regala" | "fidelity" | "none"
 */
export default function AppBottomBar({
  current = "menu",
  lang = "it",
  onOpenRate, // kept for API compatibility, but ignored — rating always goes to Google
  onOpenQR,
  children, // optional second row (e.g. "La mia selezione" on menu pages)
  spacer = true, // reserve space at the end of the page so content isn't hidden
}) {
  const navigate = useNavigate();
  // Fetch loyalty balance so the "Fidelity" button can turn into a bordeaux
  // pill showing "€ 20.00" when the guest is logged in with active credit.
  const [balanceCents, setBalanceCents] = useState(null);
  const [levelLabel, setLevelLabel] = useState("");

  useEffect(() => {
    let alive = true;
    if (!loyaltyToken()) return () => { alive = false; };
    (async () => {
      try {
        const d = await loyaltyMe();
        if (!alive) return;
        setBalanceCents(Number(d?.user?.balance_cents || 0));
        setLevelLabel(d?.user?.level?.label_it || "");
      } catch (e) {
        if (e?.response?.status === 401) loyaltyLogout();
      }
    })();
    return () => { alive = false; };
  }, []);

  const hasCredit = balanceCents != null && balanceCents > 0;
  // Only the raw amount, formatted IT (comma decimal). Prevents mobile
  // wrap that made the pill taller than the sibling buttons.
  const eurosLabel = hasCredit ? (balanceCents / 100).toFixed(2).replace(".", ",") : null;

  const openGoogleReview = () => {
    window.open("https://search.google.com/local/writereview?placeid=ChIJD64beQAJMhMREH_bYo8pbcU", "_blank");
  };

  const items = [
    {
      key: "valuta",
      label: tr(T.rate, lang),
      Icon: Star,
      iconClass: "text-amber-500",
      onClick: openGoogleReview,
      testId: "bottom-rate-btn",
    },
    {
      key: "asporto",
      label: tr(T.asporto, lang),
      Icon: ShoppingBag,
      iconClass: "text-[#4a1c17]",
      onClick: () => navigate("/asporto-app"),
      testId: "bottom-asporto-btn",
    },
    {
      key: "shop",
      label: tr(T.shop, lang),
      Icon: Store,
      iconClass: "text-[#4a1c17]",
      onClick: () => navigate("/shop-app"),
      testId: "bottom-shop-btn",
    },
    {
      key: "regala",
      label: tr({ it: "Regala", en: "Gift", es: "Regala", fr: "Offrir", de: "Schenken" }, lang),
      Icon: Gift,
      iconClass: "text-[#C48B31]",
      onClick: () => navigate("/regala"),
      testId: "bottom-gift-btn",
    },
    {
      key: "fidelity",
      label: eurosLabel || tr(T.fidelity, lang),
      Icon: hasCredit ? Wallet : Sparkles,
      iconClass: hasCredit ? "text-[#C48B31]" : "text-emerald-600",
      onClick: () => navigate("/fidelity-app"),
      testId: "bottom-fidelity-btn",
      highlight: hasCredit,
      subtitle: hasCredit ? levelLabel : "",
    },
  ].filter((it) => it.key !== current);
  // 5 pills fit a phone width exactly; with 6 (pages outside the 5 destinations)
  // switch to a horizontally scrollable row so labels are never truncated.
  const scrollable = items.length >= 6;

  return (
    <>
    {spacer && <div className={children ? "h-32 sm:h-36" : "h-16 sm:h-20"} aria-hidden />}
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#e8dcc8] bg-white/95 backdrop-blur-md" data-testid="app-bottom-bar">
      {children && <div className="max-w-3xl mx-auto px-2 sm:px-4 pt-2 sm:pt-3">{children}</div>}
      <div className={`max-w-3xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center gap-1 sm:gap-2 ${scrollable ? "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" : ""}`}>
        {items.map(({ key, label, Icon, iconClass, onClick, testId, highlight, subtitle }) => (
          <button
            key={key}
            onClick={onClick}
            data-testid={testId}
            className={`${scrollable ? "flex-none px-2.5" : "flex-1 px-1"} sm:flex-none inline-flex items-center justify-center gap-0.5 sm:gap-1.5 rounded-full sm:px-3.5 py-1.5 sm:py-2 text-[10px] sm:text-sm font-medium whitespace-nowrap min-w-0 ${
              highlight
                ? "bg-[#4a1c17] hover:bg-[#2b1410] border border-[#4a1c17] text-[#C48B31] shadow-md ring-2 ring-[#C48B31]/25"
                : "bg-white border border-[#e8dcc8] text-[#2b1410] hover:bg-[#faf3ee]"
            }`}
            title={subtitle || undefined}
          >
            <Icon className={`h-3 w-3 sm:h-4 sm:w-4 shrink-0 ${iconClass}`} />
            <span className={`truncate ${highlight ? "font-bold tabular-nums" : ""}`}>{label}</span>
          </button>
        ))}
        <div className="hidden sm:block flex-1" />
        {onOpenQR && (
          <button
            onClick={onOpenQR}
            data-testid="bottom-qr-btn"
            className="hidden sm:inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] px-2.5 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm font-medium text-[#f7ebdf] shrink-0"
          >
            <ShoppingBag className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span>{tr(T.qrMenu, lang)}</span>
          </button>
        )}
      </div>
    </div>
    </>
  );
}
